// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * What this browser can actually do, and what the interface is therefore
 * allowed to promise.
 *
 * THE FEATURE DETECT IS `'showDirectoryPicker' in window` AND NOTHING ELSE.
 *
 * `FileSystemDirectoryHandle` and `createWritable` both EXIST in Safari and in
 * Firefox — for the Origin Private File System, which is a sandbox that has
 * nothing to do with the user's folder. Detecting on either one promises folder
 * writes on two browsers that cannot do them, and the failure arrives after the
 * user has already picked a folder and reviewed a diff. Mozilla's position on
 * the folder half is `negative` and WebKit's is `oppose`, while both are
 * positive on OPFS separately — which is exactly why the misleading constructors
 * are there.
 *
 * `'webkitdirectory' in input` is the same trap on iOS: it is `true` on iOS 17,
 * where the property "can be set, but has no effect" (webkit.org/b/271705). The
 * user gets a single-file picker and there is NO ERROR. Folder selection is
 * iOS 18.4+, and the only reliable detection is that the change event delivers
 * an empty FileList.
 */

export interface Capabilities {
  /** Can we read a folder through a handle, and therefore write one back? */
  readonly directoryHandles: boolean;
  /** Can we stream an artefact straight to a chosen file, avoiding a download? */
  readonly savePicker: boolean;
  /** Can a dropped folder yield handles rather than plain files? */
  readonly dropHandles: boolean;
  /** `<input webkitdirectory>` — present, though on iOS 17 present means nothing. */
  readonly directoryInput: boolean;
  readonly clipboardRead: boolean;
  readonly share: boolean;
  readonly isIos: boolean;
  readonly isAndroidChrome: boolean;
  /** WebKit hides every dot-file from both folder-reading paths it supports. */
  readonly hidesDotfiles: boolean;
}

export function detect(): Capabilities {
  const ua = navigator.userAgent;
  const isWebKit = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  const directoryHandles = 'showDirectoryPicker' in window;

  /*
   * iPadOS reports itself as `MacIntel`, so the usual detection adds
   * `maxTouchPoints > 1` — and that is a FALSE POSITIVE on any desktop Mac with
   * a touch-capable input, or under device emulation. The browser dry-run
   * caught it: a desktop Chromium was shown "On iPhone and iPad, Safari cannot
   * write to a project folder", which is both wrong and alarming on a machine
   * where writing works perfectly.
   *
   * `showDirectoryPicker` is the reliable discriminator and it is already the
   * thing we care about: no iPhone or iPad has it, and every desktop Chromium
   * does. The touch heuristic only gets a say once the API is already absent.
   */
  const isIos = /\b(iPhone|iPad|iPod)\b/.test(ua)
    || (!directoryHandles && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return {
    directoryHandles,
    savePicker: 'showSaveFilePicker' in window,
    dropHandles: typeof DataTransferItem !== 'undefined' && 'getAsFileSystemHandle' in DataTransferItem.prototype,
    directoryInput: 'webkitdirectory' in document.createElement('input'),
    clipboardRead: typeof navigator.clipboard?.readText === 'function',
    share: typeof navigator.share === 'function',
    isIos,
    isAndroidChrome: /Android/.test(ua) && /Chrome/.test(ua),
    hidesDotfiles: isWebKit || isIos,
  };
}

/**
 * The one sentence iOS Safari users get, and it is not an apology.
 *
 * The read-only path is a complete product: a real diff against real bytes, a
 * `git apply`-clean patch, and a zip. What it genuinely cannot do is apply ignore
 * rules, because WebKit filters every dot-file out of BOTH folder-reading paths
 * — `DirectoryFileListCreator` skips hidden entries BEFORE recursing, so a whole
 * `.github/` tree is invisible, and `DOMFileSystem` returns `NotFoundError` even
 * for an explicit `getFile('.gitignore')`. That is a fact the user has to know,
 * because it changes what they should check before pasting.
 */
export const IOS_NOTE =
  'On iPhone and iPad, Safari cannot write to a project folder. unfence can read a folder you pick ' +
  '(iOS 18.4 or later), but Safari hides every dot-file and dot-folder from a web page — your ' +
  '.gitignore, .env and .github/ are invisible to it, so ignore rules cannot be applied and you ' +
  'should check the file list yourself. Changes come back as a downloaded zip and a changes.patch ' +
  'you apply with git apply from your repository root, so nothing here can damage anything. For the ' +
  'full round trip, use Chrome or Edge on a desktop.';

export const IOS_TOO_OLD =
  'This version of iOS cannot select a folder — the picker returns individual files and reports no ' +
  'error. Update to iOS 18.4 or later, or drop a .zip of your project instead.';

export const ANDROID_NOTE =
  'Chrome on Android cannot remember a folder between visits, so you will need to pick it again next time.';

export const NO_HANDLES_NOTE =
  'This browser cannot write to a folder — the File System Access API is Chromium-only, and Mozilla ' +
  'and WebKit have both declined to implement the folder half. unfence still reads your folder, ' +
  'diffs the reply against it and hands you changes.patch and updated.zip. Nothing is written here, ' +
  'so there is nothing to back up.';

export const WRITE_BLOCKED_NOTE =
  'Your browser refused permission to write to this folder. That is often an IT policy — Chrome’s ' +
  'read and write permissions are set separately, so reading can be allowed while writing is not. ' +
  'Everything else still works: take changes.patch and updated.zip below.';
